// PieChartSection.jsx
import React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const PIE_COLORS = [
  "#60a5fa",
  "#818cf8",
  "#a855f7",
  "#22c55e",
  "#f97316",
  "#ec4899",
];

const PieChartSection = ({
  title,
  configs = [],
  selectedConfigId,
  setSelectedConfigId,
  chartData = [],
  onAdd,
  onDelete, // ✅ NEW
}) => {
  const selectedConfig = configs.find((c) => c._id === selectedConfigId);

  const handleConfigChange = (e) => {
    setSelectedConfigId && setSelectedConfigId(e.target.value);
  };

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const item = chartData[index];
    if (!item) return null;

    const value =
      typeof item.value === "number"
        ? item.value.toFixed(1)
        : (percent * 100).toFixed(1);

    return (
      <text
        x={x}
        y={y}
        fill="#60a5fa"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${item.label}: ${value}%`}
      </text>
    );
  };

  return (
    <div className="rounded-2xl bg-white/80 border shadow p-4 w-full">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <h3
            className="text-sm font-semibold text-slate-800 truncate"
            title={selectedConfig?.heading || title}
          >
            {selectedConfig?.heading || title || "Pie Chart"}
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {configs.length > 0 && (
            <select
              className="rounded-full border px-2 py-1 text-[11px] max-w-[220px]"
              value={selectedConfigId}
              onChange={handleConfigChange}
            >
              {configs.map((cfg) => (
                <option key={cfg._id} value={cfg._id}>
                  {cfg.heading}
                </option>
              ))}
            </select>
          )}

          {/* ✅ Delete button */}
          {selectedConfigId && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
            >
              🗑 Delete
            </button>
          )}

          <button
            onClick={onAdd}
            className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            ＋ Add
          </button>
        </div>
      </div>

      <div className="h-56 mt-3">
        {chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius="55%"
                outerRadius="75%"
                paddingAngle={2}
                labelLine={false}
                label={renderLabel}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, _name, payload) => {
                  const item = payload?.payload;
                  const v =
                    typeof value === "number"
                      ? value.toFixed(1)
                      : Number(value || 0).toFixed(1);
                  return [`${v}%`, item?.label];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-xs text-slate-400">
            No pie data available
          </div>
        )}
      </div>
    </div>
  );
};

export default PieChartSection;
